import React, { useEffect, useMemo, useRef, useState } from 'react';

type TaskGroupOption = {
  code: string;
  label: string;
};

type FilterOption = {
  value: string;
  label: string;
  searchText?: string;
};

type CaseStatusCard = {
  key: string;
  label: string;
  count: number;
};

type SearchableFilterProps = {
  styles: Record<string, any>;
  label: string;
  value: string;
  allLabel: string;
  searchPlaceholder: string;
  emptyLabel: string;
  options: FilterOption[];
  onChange: (value: string) => void;
};

function SearchableFilter({
  styles,
  label,
  value,
  allLabel,
  searchPlaceholder,
  emptyLabel,
  options,
  onChange
}: SearchableFilterProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      setQuery('');
    }
  }, [open]);

  const selectedLabel =
    value === 'all'
      ? allLabel
      : options.find(option => option.value === value)?.label || allLabel;

  const filteredOptions = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return options;
    return options.filter(option => {
      const haystack = `${option.label} ${option.searchText || ''}`.toLowerCase();
      return haystack.includes(needle);
    });
  }, [options, query]);

  return (
    <div style={styles.searchableFilter} ref={rootRef}>
      <div style={styles.searchableFilterLabel}>{label}</div>
      <button
        type="button"
        style={{
          ...styles.searchableFilterTrigger,
          ...(open ? styles.searchableFilterTriggerOpen : {})
        }}
        onClick={() => setOpen(current => !current)}
      >
        <span style={styles.searchableFilterTriggerValue}>{selectedLabel}</span>
        <span style={styles.searchableFilterTriggerIcon}>{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div style={styles.searchableFilterPopover}>
          <input
            type="text"
            placeholder={searchPlaceholder}
            style={styles.searchableFilterSearchInput}
            value={query}
            onChange={event => setQuery(event.target.value)}
            autoFocus
          />
          <div style={styles.searchableFilterOptions}>
            <button
              type="button"
              style={{
                ...styles.searchableFilterOption,
                ...(value === 'all' ? styles.searchableFilterOptionActive : {})
              }}
              onClick={() => {
                onChange('all');
                setOpen(false);
              }}
            >
              {allLabel}
            </button>
            {filteredOptions.length === 0 ? (
              <div style={styles.searchableFilterEmpty}>{emptyLabel}</div>
            ) : (
              filteredOptions.map(option => (
                <button
                  key={option.value}
                  type="button"
                  style={{
                    ...styles.searchableFilterOption,
                    ...(value === option.value ? styles.searchableFilterOptionActive : {})
                  }}
                  onClick={() => {
                    onChange(option.value);
                    setOpen(false);
                  }}
                >
                  {option.label}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

type TechDashboardCasesHeaderProps = {
  styles: Record<string, any>;
  canOpenInternalJobs: boolean;
  onCreateCase: () => void;
  searchQuery: string;
  onSearchQueryChange: (value: string) => void;
  statusFilter: string;
  onStatusFilterChange: (value: string) => void;
  priorityFilter: string;
  onPriorityFilterChange: (value: string) => void;
  assignmentFilter: 'all' | 'assigned' | 'unassigned';
  onAssignmentFilterChange: (value: 'all' | 'assigned' | 'unassigned') => void;
  caseUserFilter: string;
  onCaseUserFilterChange: (value: string) => void;
  caseUserOptions: FilterOption[];
  canUseCaseUserFilter: boolean;
  taskGroupFilter: string;
  onTaskGroupFilterChange: (value: string) => void;
  taskGroups: TaskGroupOption[];
  customerFilter: string;
  onCustomerFilterChange: (value: string) => void;
  caseCustomerOptions: FilterOption[];
  deviceModelFilter: string;
  onDeviceModelFilterChange: (value: string) => void;
  caseDeviceModelOptions: FilterOption[];
  deviceSerialFilter: string;
  onDeviceSerialFilterChange: (value: string) => void;
  caseDeviceSerialOptions: FilterOption[];
  caseSortOrder: 'newest' | 'oldest';
  onCaseSortOrderChange: (value: 'newest' | 'oldest') => void;
  hasActiveFilters: boolean;
  activeFilterCount: number;
  onClearFilters: () => void;
  topPanelCollapsed: boolean;
  onToggleTopPanel: () => void;
};

export const TechDashboardCasesHeader = React.memo(function TechDashboardCasesHeader({
  styles,
  canOpenInternalJobs,
  onCreateCase,
  searchQuery,
  onSearchQueryChange,
  statusFilter,
  onStatusFilterChange,
  priorityFilter,
  onPriorityFilterChange,
  assignmentFilter,
  onAssignmentFilterChange,
  caseUserFilter,
  onCaseUserFilterChange,
  caseUserOptions,
  canUseCaseUserFilter,
  taskGroupFilter,
  onTaskGroupFilterChange,
  taskGroups,
  customerFilter,
  onCustomerFilterChange,
  caseCustomerOptions,
  deviceModelFilter,
  onDeviceModelFilterChange,
  caseDeviceModelOptions,
  deviceSerialFilter,
  onDeviceSerialFilterChange,
  caseDeviceSerialOptions,
  caseSortOrder,
  onCaseSortOrderChange,
  hasActiveFilters,
  activeFilterCount,
  onClearFilters,
  topPanelCollapsed,
  onToggleTopPanel
}: TechDashboardCasesHeaderProps) {
  return (
    <div style={styles.header}>
      <div style={styles.casesHeaderPanel}>
        <div style={styles.casesHeaderTop}>
          <div style={styles.casesTitleWrap}>
            <h1 style={styles.pageTitle}>Cases</h1>
            {!topPanelCollapsed && (
              <div style={styles.pageSubtitle}>
                Manage queue, triage status, and routing from one workspace.
              </div>
            )}
          </div>
          <div style={styles.casesHeaderActions}>
            <button
              type="button"
              style={styles.casesFilterToggleButton}
              onClick={onToggleTopPanel}
            >
              {topPanelCollapsed
                ? `Show Top Panel${activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}`
                : 'Hide Top Panel'}
            </button>
            {canOpenInternalJobs && (
              <button style={styles.newCaseButton} onClick={onCreateCase}>
                + New Case
              </button>
            )}
          </div>
        </div>

        {topPanelCollapsed ? (
          <div style={styles.casesTopPanelCollapsedBar}>
            <div style={styles.casesTopPanelCollapsedText}>
              {hasActiveFilters
                ? `${activeFilterCount} filter${activeFilterCount === 1 ? '' : 's'} active. Expand the top panel to adjust search and queue filters.`
                : 'Top panel hidden. Expand it to search, filter, or review queue stats.'}
            </div>
            {hasActiveFilters && (
              <button
                type="button"
                style={styles.caseFilterActionButton}
                onClick={onClearFilters}
              >
                Clear Filters
              </button>
            )}
          </div>
        ) : (
          <div style={styles.controlStack}>
            <div style={styles.searchRow}>
              <input
                type="text"
                placeholder="Search cases..."
                style={styles.searchInput}
                value={searchQuery}
                onChange={event => onSearchQueryChange(event.target.value)}
              />
            </div>
            <div style={styles.casesFilterPanels}>
              <div style={styles.casesFilterPanel}>
                <div style={styles.casesFilterPanelHeader}>
                  <div>
                    <div style={styles.casesFilterPanelTitle}>Queue Filters</div>
                    <div style={styles.casesFilterPanelHint}>
                      {canUseCaseUserFilter
                        ? 'Status, priority, assignment, user, task type, and timeline order.'
                        : 'Status, priority, assignment, task type, and timeline order.'}
                    </div>
                  </div>
                  <button
                    type="button"
                    style={{
                      ...styles.caseFilterActionButton,
                      ...(hasActiveFilters ? {} : styles.caseFilterActionButtonDisabled)
                    }}
                    onClick={onClearFilters}
                    disabled={!hasActiveFilters}
                  >
                    Clear Filters
                  </button>
                </div>
                <div style={styles.casesFilterGrid}>
                  <select
                    style={styles.filterSelect}
                    value={statusFilter}
                    onChange={event => onStatusFilterChange(event.target.value)}
                  >
                    <option value="all">All Status</option>
                    <option value="NEW">Open</option>
                    <option value="IN_PROGRESS">In Progress</option>
                    <option value="WAITING_CUSTOMER">Waiting Customer</option>
                    <option value="WAITING_PARTS">Waiting Parts</option>
                    <option value="ON_HOLD">On Hold</option>
                    <option value="RESOLVED">Resolved</option>
                    <option value="CLOSED">Closed</option>
                  </select>
                  <select
                    style={styles.filterSelect}
                    value={priorityFilter}
                    onChange={event => onPriorityFilterChange(event.target.value)}
                  >
                    <option value="all">All Priorities</option>
                    <option value="CRITICAL">Critical</option>
                    <option value="HIGH">High</option>
                    <option value="MEDIUM">Standard</option>
                    <option value="LOW">Low</option>
                  </select>
                  <select
                    style={styles.filterSelect}
                    value={assignmentFilter}
                    onChange={event =>
                      onAssignmentFilterChange(event.target.value as 'all' | 'assigned' | 'unassigned')
                    }
                  >
                    <option value="all">All Assignments</option>
                    <option value="assigned">Assigned</option>
                    <option value="unassigned">Unassigned</option>
                  </select>
                  {canUseCaseUserFilter && (
                    <select
                      style={styles.filterSelect}
                      value={caseUserFilter}
                      onChange={event => onCaseUserFilterChange(event.target.value)}
                    >
                      <option value="all">All Users</option>
                      {caseUserOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  )}
                  <select
                    style={styles.filterSelect}
                    value={taskGroupFilter}
                    onChange={event => onTaskGroupFilterChange(event.target.value)}
                  >
                    <option value="all">All Task Types</option>
                    <option value="UNASSIGNED">No Task</option>
                    {taskGroups.map(group => (
                      <option key={group.code} value={group.code}>
                        {group.label}
                      </option>
                    ))}
                  </select>
                  <select
                    style={styles.filterSelect}
                    value={caseSortOrder}
                    onChange={event => onCaseSortOrderChange(event.target.value as 'newest' | 'oldest')}
                  >
                    <option value="newest">Newest first</option>
                    <option value="oldest">Oldest first</option>
                  </select>
                </div>
              </div>

              <div style={styles.casesFilterPanel}>
                <div style={styles.casesFilterPanelHeader}>
                  <div style={styles.casesFilterPanelTitle}>Customer And Device History</div>
                  <div style={styles.casesFilterPanelHint}>
                    Search large customer, model, serial, and asset histories without cluttering the main queue filters.
                  </div>
                </div>
                <div style={styles.casesSearchableFilterGrid}>
                  <SearchableFilter
                    styles={styles}
                    label="Customer"
                    value={customerFilter}
                    allLabel="All Customers"
                    searchPlaceholder="Search customers..."
                    emptyLabel="No matching customers"
                    options={caseCustomerOptions}
                    onChange={onCustomerFilterChange}
                  />
                  <SearchableFilter
                    styles={styles}
                    label="Device Model"
                    value={deviceModelFilter}
                    allLabel="All Device Models"
                    searchPlaceholder="Search device models..."
                    emptyLabel="No matching device models"
                    options={caseDeviceModelOptions}
                    onChange={onDeviceModelFilterChange}
                  />
                  <SearchableFilter
                    styles={styles}
                    label="Device Serial / Asset"
                    value={deviceSerialFilter}
                    allLabel="All Devices"
                    searchPlaceholder="Search serial or asset numbers..."
                    emptyLabel="No matching devices"
                    options={caseDeviceSerialOptions}
                    onChange={onDeviceSerialFilterChange}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

type TechDashboardCasesStatsProps = {
  styles: Record<string, any>;
  caseStatusCards: CaseStatusCard[];
  statusFilter: string;
  onStatusFilterChange: (value: string) => void;
  caseStats: { needsVerification: number };
};

export const TechDashboardCasesStats = React.memo(function TechDashboardCasesStats({
  styles,
  caseStatusCards,
  statusFilter,
  onStatusFilterChange,
  caseStats
}: TechDashboardCasesStatsProps) {
  return (
    <>
      <div style={styles.statsGridWrap}>
        <div style={styles.statsGrid}>
          {caseStatusCards.map(card => (
            <button
              key={card.key}
              type="button"
              style={{
                ...styles.statCard,
                ...styles.statCardInteractive,
                ...(statusFilter === card.key.toLowerCase() || statusFilter === card.key
                  ? styles.statCardActive
                  : {})
              }}
              onClick={() => onStatusFilterChange(card.key === 'all' ? 'all' : card.key)}
            >
              <div style={styles.statNumber}>{card.count}</div>
              <div style={styles.statLabel}>{card.label}</div>
            </button>
          ))}
        </div>
      </div>
      {caseStats.needsVerification > 0 && (
        <div style={styles.statsAuxRow}>
          <div style={{ ...styles.statCard, minWidth: '220px' }}>
            <div style={{ ...styles.statNumber, color: '#f59e0b' }}>{caseStats.needsVerification}</div>
            <div style={styles.statLabel}>Needs Model Verification</div>
          </div>
        </div>
      )}
    </>
  );
});
